import React, { useEffect, useRef, useState } from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import { Box, IconButton, Toolbar, useMediaQuery } from '@mui/material';
import { PATHS } from 'AppPaths';
import { useAtom } from 'jotai';
import Banner from 'layout/Banner';
import LoginModal from 'layout/Navbar/LoginModal';
import WaitingListModal from 'layout/Navbar/WaitingListModal';
import platform from 'platform';
import {
  headerHeightAtom,
  isBannerOpenAtom,
  isWatingModalOpenAtom,
  mapImgAtom,
} from 'store';
import { useAuth } from 'util/auth.js';
import { getNoImg } from 'util/db.js';
import { Link, useRouter } from 'util/router';

import DesktopNavLinks from './DesktopNavLinks';
import LoggedInNavItems from './LoggedInNavItems';
import LoggedOutNavItems from './LoggedOutNavItems';
import { AppBar, PickerLogoTitle, StyledImage } from './styles';

import logo from 'assets/icons/picker_logo.png';

const Navbar = ({ open, ToggleDrawer, drawerWidth, setIsSurferOpen }) => {
  const auth = useAuth();
  const router = useRouter();
  const headerRef = useRef(null);

  const [, setMapImg] = useAtom(mapImgAtom);
  const [isWaitingModalOpen, setIsWaitingModalOpen] = useAtom(
    isWatingModalOpenAtom
  );
  const [, setHeaderHeight] = useAtom(headerHeightAtom);
  const [isBannerOpen, setIsBannerOpen] = useAtom(isBannerOpenAtom);

  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  const isMediumOrSmaller = useMediaQuery((theme) =>
    theme.breakpoints.down('md')
  );
  const isSmallMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const openLoginModal = () => setIsLoginModalOpen(true);
  const closeLoginModal = () => setIsLoginModalOpen(false);
  const closeWaitingModal = () => setIsWaitingModalOpen(false);

  useEffect(() => {
    getNoImg().then((result) => {
      setMapImg(result.images);
    });
  }, [setMapImg]);

  useEffect(() => {
    const storedBannerState = JSON.parse(localStorage.getItem('isBannerOpen'));
    const shouldShowBanner =
      storedBannerState && auth.user && platform.name === 'Chrome';
    setIsBannerOpen(Boolean(shouldShowBanner));
  }, [auth.user, setIsBannerOpen]);

  useEffect(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.clientHeight - 2);
    }
  }, [isBannerOpen, isMediumOrSmaller, setHeaderHeight]);

  const shouldShowNavContent =
    router.pathname !== PATHS.TRYFORFREE && router.pathname !== PATHS.RECIPES;

  return (
    <>
      <AppBar
        position="fixed"
        color="inherit"
        open={open}
        drawerWidth={drawerWidth}
        ref={headerRef}
      >
        {isBannerOpen && (
          <Banner
            setIsSurferOpen={setIsSurferOpen}
            setIsBannerOpen={setIsBannerOpen}
          />
        )}
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box
            display="flex"
            alignItems="center"
            sx={{ paddingTop: '15px', paddingBottom: '15px' }}
          >
            {auth.user && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={ToggleDrawer}
                edge="start"
                sx={{ height: '40px', mr: 2, ...(open && { display: 'none' }) }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Link
              to="/"
              style={{
                textDecoration: 'none',
                color: 'inherit',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <StyledImage src={logo} alt="Picker logo" />
              <PickerLogoTitle variant="h5">Picker</PickerLogoTitle>
            </Link>
          </Box>

          {shouldShowNavContent && (
            <>
              {!auth.user && auth.user !== null && <DesktopNavLinks />}

              <Box display="flex" alignItems="center" gap={{ xs: 1, md: 2 }}>
                {!auth.user && auth.user !== null ? (
                  <LoggedOutNavItems
                    isSmallMobile={isSmallMobile}
                    onLoginClick={openLoginModal}
                  />
                ) : auth.user ? (
                  <LoggedInNavItems auth={auth} />
                ) : null}
              </Box>
            </>
          )}
        </Toolbar>
      </AppBar>

      <LoginModal
        open={isLoginModalOpen}
        handleClose={closeLoginModal}
        type="signin"
        title="Welcome back"
      />
      <WaitingListModal
        open={isWaitingModalOpen}
        handleClose={closeWaitingModal}
      />
    </>
  );
};

export default Navbar;
